import React, { type FormEvent, useCallback, useState } from 'react'
import { Form } from 'react-bootstrap'
import { Button } from '../../../components/Button/Button'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { type OAuth2App, useUpdateOAuth2AppMutation } from '../../../store/auth/auth'
import { addToast } from '../../../store/toast/toastSlice'
import { isValidUrl } from '../../../common/urlValidation'
import { RedirectUrisFormFields } from '../components/RedirectUrisFormFields'
import { type RedirectUri, toRedirectUris } from '../components/redirectUri'

interface ManageAppRedirectUrisTabProps {
  oAuth2App: OAuth2App
}

export const ManageAppRedirectUrisTab: React.FC<ManageAppRedirectUrisTabProps> = ({ oAuth2App }) => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const [updateApp, { isLoading: isSaving }] = useUpdateOAuth2AppMutation()

  const [uris, setUris] = useState<RedirectUri[]>(() => toRedirectUris(oAuth2App.redirectUris.length > 0 ? oAuth2App.redirectUris : ['']))
  const [validated, setValidated] = useState<boolean>(false)
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false)

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      setValidated(true)
      setSaveSuccess(false)

      const nonEmptyUris = uris.map((uri) => uri.value).filter((value) => value.trim() !== '')
      if (nonEmptyUris.length === 0) {
        return
      }

      const allValid = nonEmptyUris.every((uri) => isValidUrl(uri))
      if (!allValid) {
        return
      }

      try {
        await updateApp({
          accountId,
          clientId: oAuth2App.clientId,
          payload: { redirectUris: nonEmptyUris },
        }).unwrap()

        setSaveSuccess(true)
      } catch {
        dispatch(addToast({ variant: 'error', title: 'Error', messages: ['Failed to save changes. Please try again.'] }))
      }
    },
    [accountId, oAuth2App.clientId, updateApp, uris, dispatch],
  )

  const nonEmptyUris = uris.map((u) => u.value).filter((value) => value.trim() !== '')
  const hasChanges = nonEmptyUris.length !== oAuth2App.redirectUris.length || nonEmptyUris.some((value, i) => value !== oAuth2App.redirectUris[i])

  const handleDiscard = useCallback(() => {
    setUris(toRedirectUris(oAuth2App.redirectUris.length > 0 ? oAuth2App.redirectUris : ['']))
    setValidated(false)
    setSaveSuccess(false)
  }, [oAuth2App.redirectUris])

  const isExternal = oAuth2App.type === 'external'
  const subtitle = isExternal
    ? 'Sandbox: URI changes take effect immediately. Production: changes trigger a review.'
    : 'URIs where your app can receive authorization callbacks.'

  return (
    <Form noValidate validated={validated} onSubmit={handleSubmit}>
      <h5 className='fw-bold mb-1'>Redirect URIs</h5>
      <p className='text-muted mb-4'>{subtitle}</p>

      <RedirectUrisFormFields uris={uris} setUris={setUris} validated={validated} />

      {saveSuccess ? <div className='text-success small mb-3 mt-3'>Redirect URIs saved successfully.</div> : null}

      <div className='d-flex justify-content-end gap-3 mt-4'>
        <Button variant='secondary' className='fw-bold' onClick={handleDiscard} disabled={!hasChanges || isSaving}>
          Discard
        </Button>
        <Button type='submit' variant='primary' className='fw-bold' disabled={!hasChanges || isSaving} loading={isSaving}>
          Save changes
        </Button>
      </div>
    </Form>
  )
}
